import React, { useEffect, useRef, useState } from "react";
import { toast, Toaster } from "sonner";
import { useSearchParams } from "react-router-dom";
import DashboardShell from "../../components/layout/DashboardShell";
import { Button, Card, Input, Pill, Select } from "../../components/common/Primitives";
import AdminApprovalBadge from "../../components/common/AdminApprovalBadge";
import SandboxKycPanel from "../../components/kyc/SandboxKycPanel";
import api, { formatApiError } from "../../lib/api";
import useFocusHighlight from "../../lib/useFocusHighlight";
import {
  ShieldCheck, UserCircle2, Building2, Upload, Trash2, FileText, Download, Info,
} from "lucide-react";

/**
 * Investor Verification page — splits identity from firm KYC into 2 tabs so
 * founders / investors can no longer conflate them with the Profile page.
 *
 *   Tab A · Individual Verification — Personal Aadhaar (DigiLocker) + PAN
 *   Tab B · Business Verification   — Firm docs (SEBI/AIF, incorporation,
 *                                     source-of-funds, etc.).
 *                                     Greyed out for Angel / HNI / Retail
 *                                     types where a firm doesn't exist.
 */

const KYC_CATEGORIES = [
  "PAN Card", "Aadhaar / Passport", "Entity Incorporation", "SEBI / AIF Registration",
  "NBFC License", "Bank RBI License", "Authorised Signatory Declaration",
  "Fund Deck / Brochure", "Source of Funds Declaration", "Compliance Declaration",
];

const INDIVIDUAL_TYPES = new Set(["Retail investor", "Angel investor", "HNI"]);

const TABS = [
  { id: "individual", label: "Individual Verification", icon: UserCircle2 },
  { id: "business",   label: "Business Verification",   icon: Building2 },
];

const toneFor = (s) => {
  if (s === "Accepted" || s === "Approved") return "success";
  if (s === "Rejected") return "danger";
  if (s === "On Hold" || s === "Under Review") return "warn";
  return "muted";
};

function formatBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function InvestorVerification() {
  useFocusHighlight();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "business" ? "business" : "individual";
  const [tab, setTab] = useState(initialTab);

  const [me, setMe] = useState(null);
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState(KYC_CATEGORIES[0]);
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    setMe((await api.get("/investor/me")).data);
    setDocs((await api.get("/investor/kyc/documents")).data || []);
  };
  useEffect(() => { load(); }, []);

  const investor = me?.investor || {};
  const investorType = investor.investor_type || "";
  const isIndividualInvestor = INDIVIDUAL_TYPES.has(investorType);
  const kycStatus = investor.kyc_status || "Not Submitted";

  const switchTab = (id) => {
    setTab(id);
    const p = new URLSearchParams(searchParams);
    if (id === "individual") p.delete("tab"); else p.set("tab", id);
    setSearchParams(p, { replace: true });
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file first.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("category", category);
      fd.append("note", note || "");
      fd.append("file", file);
      await api.post("/investor/kyc/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} uploaded.`);
      setFile(null); setNote("");
      if (inputRef.current) inputRef.current.value = "";
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/investor/kyc/documents/${id}`); toast.success("Deleted."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const download = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.doc_id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = doc.filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitKyc = async () => {
    try {
      await api.post("/investor/kyc/submit");
      toast.success("KYC submitted. Under review.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DashboardShell title="Verification" subtitle="INVESTOR · TRUST & COMPLIANCE">
      <Toaster richColors position="top-right" />

      {/* Status ribbon */}
      <Card testId="verification-status-ribbon" className="mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <ShieldCheck size={22} className="text-gold shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
              Overall verification status
            </div>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <Pill tone={toneFor(kycStatus)} data-testid="verification-overall-status">{kycStatus}</Pill>
              <AdminApprovalBadge
                entity={{
                  kyc_status: investor.kyc_status,
                  admin_temp_approved: investor.admin_temp_approved,
                  admin_approved_at: investor.admin_approved_at,
                  admin_approved_by: investor.admin_approved_by,
                  founder_id_verified: investor.identity_verified,
                  cin: investor.cin,
                  cin_verified_at: investor.cin_verified_at,
                  gstin: investor.gstin,
                  gstin_verified_at: investor.gstin_verified_at,
                }}
                size="sm"
                testId="verification-admin-approval"
              />
              {investorType && (
                <span className="text-xs text-ink-muted flex items-center gap-1">
                  · <UserCircle2 size={12} /> {investorType}
                </span>
              )}
            </div>
          </div>
          <Button
            onClick={submitKyc}
            disabled={kycStatus === "Approved" || kycStatus === "Under Review"}
            data-testid="investor-kyc-submit"
          >
            {kycStatus === "Approved" ? "KYC Approved" :
              kycStatus === "Under Review" ? "Under Review" : "Submit for KYC Review"}
          </Button>
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6 border-b border-navy/10" data-testid="verification-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => switchTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 -mb-px border-b-2 transition-colors text-sm ${
                active
                  ? "border-emerald-600 text-emerald-700 font-heading"
                  : "border-transparent text-ink-muted hover:text-ink"
              }`}
              data-testid={`verification-tab-${t.id}`}
            >
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "individual" && (
        <div className="space-y-6" data-testid="verification-individual-panel">
          <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
            Personal KYC · Powered by DigiLocker & Income Tax
          </div>
          <div className="text-sm text-ink-soft">
            Verify your identity as the authorised signatory / individual investor. Aadhaar via
            DigiLocker + PAN are pulled instantly and locked once confirmed.
          </div>
          <SandboxKycPanel showCompany={false} />

          {!isIndividualInvestor && (
            <div className="text-xs text-ink-muted italic pt-2">
              Firm registrations, SEBI/AIF proofs, and source-of-funds?
              <button
                onClick={() => switchTab("business")}
                className="ml-1 text-emerald-700 hover:underline font-heading"
                data-testid="link-to-business-tab"
              >
                Switch to Business Verification →
              </button>
            </div>
          )}
        </div>
      )}

      {tab === "business" && (
        <div className="space-y-6" data-testid="verification-business-panel">
          {isIndividualInvestor ? (
            <Card testId="business-not-applicable">
              <div className="flex items-start gap-3">
                <Info size={20} className="text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <div className="font-heading text-ink text-lg">Not applicable for individual investors</div>
                  <div className="text-sm text-ink-soft mt-2">
                    You&apos;re registered as an <b>{investorType}</b>. Business Verification only
                    applies to fund structures — VC / PE / AIF / Family Office / NBFC / Bank /
                    Strategic Investor / Debt Fund. Your Individual Verification tab is all you
                    need to unlock institutional-tier startup access.
                  </div>
                  <div className="text-xs text-ink-muted mt-3">
                    If you invest through a firm, update your <b>Investor Type</b> on the
                    <a href="/investor/profile" className="text-emerald-700 hover:underline mx-1">Profile page</a>
                    and this tab will activate.
                  </div>
                </div>
              </div>
            </Card>
          ) : (
            <>
              <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
                Firm Verification · Documents & Regulator Licenses
              </div>
              <div className="text-sm text-ink-soft">
                Upload firm-level proofs — entity incorporation, SEBI / AIF / RBI registrations,
                and source-of-funds declaration. Our team verifies before granting institutional
                access.
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1">
                  <Card testId="investor-kyc-status">
                    <div className="flex items-center gap-3 mb-3">
                      <ShieldCheck className="text-gold" size={20} />
                      <div>
                        <div className="overline">Current Status</div>
                        <div className="mt-1"><Pill tone={toneFor(kycStatus)}>{kycStatus}</Pill></div>
                      </div>
                    </div>
                    <ul className="mt-2 space-y-1.5 text-xs text-ink-muted">
                      <li>• Authorised signatory PAN + Aadhaar/Passport</li>
                      <li>• Entity incorporation (LLP/Pvt Ltd/Trust)</li>
                      <li>• Regulatory license (SEBI / RBI / AIF as applicable)</li>
                      <li>• Source-of-funds declaration</li>
                    </ul>
                  </Card>

                  <form onSubmit={upload} className="mt-4 border border-white/10 bg-white/70 backdrop-blur p-6 space-y-4" data-testid="investor-kyc-upload-form">
                    <div className="overline">Upload Document</div>
                    <Select label="Category" value={category} onChange={(e) => setCategory(e.target.value)} testId="ikyc-cat">
                      {KYC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </Select>
                    <div>
                      <div className="overline mb-1.5">File</div>
                      <input
                        ref={inputRef}
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        className="w-full text-sm text-ink-soft file:mr-3 file:px-3 file:py-2 file:border-0 file:bg-gold file:text-ink-bg file:font-heading file:font-semibold file:cursor-pointer file:hover:bg-gold-hover"
                        data-testid="ikyc-file"
                      />
                      {file && <div className="text-[11px] font-mono text-ink-muted mt-2">{file.name} · {formatBytes(file.size)}</div>}
                    </div>
                    <Input label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} testId="ikyc-note" />
                    <Button type="submit" disabled={busy || !file} className="w-full flex items-center justify-center gap-2" data-testid="ikyc-upload-btn">
                      <Upload size={14} /> {busy ? "Uploading…" : "Upload"}
                    </Button>
                  </form>
                </div>

                <div className="lg:col-span-2 border border-white/10 bg-white/70 backdrop-blur" data-testid="ikyc-docs-list">
                  <div className="p-4 border-b border-white/10 overline">KYC Documents · {docs.length}</div>
                  {docs.length === 0 ? (
                    <div className="p-10 text-center text-ink-muted text-sm">No KYC documents uploaded yet.</div>
                  ) : (
                    <div className="divide-y divide-white/10">
                      {docs.map((d) => (
                        <div key={d.doc_id} className="flex items-center justify-between px-4 py-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <FileText size={16} className="text-gold shrink-0" />
                            <div className="min-w-0">
                              <div className="text-ink text-sm font-heading truncate">{d.filename}</div>
                              <div className="text-[11px] font-mono text-ink-muted uppercase tracking-wider">{d.category}{d.size ? ` · ${formatBytes(d.size)}` : ""}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Pill tone={toneFor(d.status)}>{d.status}</Pill>
                            <button onClick={() => download(d)} className="text-ink-muted hover:text-gold transition-colors" title="Download" data-testid={`ikyc-download-${d.doc_id}`}><Download size={14} /></button>
                            <button onClick={() => remove(d.doc_id)} className="text-ink-muted hover:text-rose-400 transition-colors" title="Delete" data-testid={`ikyc-delete-${d.doc_id}`}><Trash2 size={14} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </DashboardShell>
  );
}
